'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { BillingAddress } from '@/services/CustomerApiService';
import { useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import BillingAddressModal from './BillingAddressModal';
import { cardVariants } from '@/lib/animations';
import type { Country } from '@/components/ui/CountryCitySelect';

interface BillingAddressesListProps {
  addresses: BillingAddress[];
  onAdd: (data: BillingAddress) => Promise<void>;
  onEdit: (id: number, data: BillingAddress) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onSetDefault: (id: number) => Promise<void>;
  translations?: Record<string, string>;
  countries: Country[];
}

export default function BillingAddressesList({
  addresses,
  onAdd,
  onEdit,
  onDelete,
  onSetDefault,
  translations = {},
  countries,
}: BillingAddressesListProps) {
  const t = useCustomerPanelTranslations(translations);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<BillingAddress | null>(null);

  const handleAdd = () => {
    setEditingAddress(null);
    setIsModalOpen(true);
  };

  const handleEdit = (address: BillingAddress) => {
    setEditingAddress(address);
    setIsModalOpen(true);
  };

  const handleSave = async (data: BillingAddress) => {
    if (editingAddress?.id) {
      await onEdit(editingAddress.id, data);
    } else {
      await onAdd(data);
    }
    setIsModalOpen(false);
    setEditingAddress(null);
  };

  const handleDelete = async (id: number) => {
    if (confirm(t('customer_panel.billing.confirm_delete', 'Are you sure you want to delete this billing address?'))) {
      await onDelete(id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <motion.button
          type="button"
          onClick={handleAdd}
          className="px-6 py-2 bg-alsero-first text-white rounded-md hover:bg-alsero-second focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {t('customer_panel.billing.add_address', 'Add Billing Address')}
        </motion.button>
      </div>

      {addresses.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-600">
            {t('customer_panel.billing.no_addresses', 'No billing addresses found. Add your first billing address.')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses.map((address, index) => (
            <motion.div
              key={address.id || index}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              custom={index}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  {address.is_default && (
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full mb-2">
                      {t('customer_panel.billing.default', 'Default')}
                    </span>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {address.title || (address.type === 'individual' 
                      ? `${address.first_name} ${address.last_name}` 
                      : address.company_name)}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {address.type === 'individual' 
                      ? t('customer_panel.billing.individual', 'Individual')
                      : t('customer_panel.billing.corporate', 'Corporate')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button type="button"
                    onClick={() => handleEdit(address)}
                    className="text-alsero-first hover:text-alsero-second transition-colors"
                    aria-label={t('customer_panel.common.edit', 'Edit')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button type="button"
                    onClick={() => address.id && handleDelete(address.id)}
                    className="text-red-600 hover:text-red-700 transition-colors"
                    aria-label={t('customer_panel.common.delete', 'Delete')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                {address.type === 'corporate' && address.company_name && (
                  <p><strong>{t('customer_panel.billing.company_name', 'Company')}:</strong> {address.company_name}</p>
                )}
                {address.type === 'corporate' && address.tax_number && (
                  <p><strong>{t('customer_panel.billing.tax_number', 'Tax Number')}:</strong> {address.tax_number}</p>
                )}
                {address.address && (
                  <p><strong>{t('customer_panel.profile.address', 'Address')}:</strong> {address.address}</p>
                )}
                {(address.city || address.country) && (
                  <p>
                    {address.city && address.country 
                      ? `${address.city}, ${address.country}`
                      : address.city || address.country}
                    {address.postal_code && ` (${address.postal_code})`}
                  </p>
                )}
                {address.phone && (
                  <p><strong>{t('customer_panel.profile.phone', 'Phone')}:</strong> {address.phone}</p>
                )}
                {address.email && (
                  <p><strong>{t('customer_panel.profile.email', 'Email')}:</strong> {address.email}</p>
                )}
              </div>

              {!address.is_default && address.id && (
                <div className="mt-4">
                  <button type="button"
                    onClick={() => onSetDefault(address.id!)}
                    className="text-sm text-alsero-first hover:text-alsero-second underline"
                  >
                    {t('customer_panel.billing.set_as_default', 'Set as default')}
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <BillingAddressModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAddress(null);
        }}
        onSave={handleSave}
        initialData={editingAddress}
        translations={translations}
        countries={countries}
      />
    </div>
  );
}
